/**
 * Who can sign in (T642 M6, R7) — the operator admin surface.
 *
 * THE REQUIREMENT IT ANSWERS: "Alex can add and remove a named operator without
 * a developer." Until now that took `npm run operator` on a laptop with the
 * production credential. The CLI stays as the break-glass path — for the case
 * where this screen is exactly what nobody can log in to.
 *
 * ⛔ THE PRIVILEGE MODEL IS FLAT AND EVERY ACT IS RECORDED. Any signed-in
 * operator can do everything here. That is safe only because the server writes
 * an `operator_events` row naming the person who acted, which is why the recent
 * activity list at the bottom is part of the feature and not decoration: it is
 * the surface on which a peer takeover becomes visible to the people it affects.
 *
 * ⛔ THERE IS NO "TYPE A PASSWORD FOR THEM" FIELD, and adding one would undo the
 * policy: passwords are GENERATED server-side and shown once (bin/operator.ts's
 * argument, which core/crypto.ts enforces in every environment). The one-time
 * panel is therefore load-bearing, not a nicety — nothing can read the value
 * back.
 *
 * ⛔ THE ONE-TIME PASSWORD IS AN INPUT VALUE, NEVER RENDERED TEXT, and that is a
 * LEAK FIX rather than a styling choice (T642 M6 round B, security review LOW).
 * The kiosk feedback widget photographs the live DOM into a JPEG, and its
 * redaction (feedback/widget.ts:560-566 `onCloneEachNode`) blanks the `value`
 * of every cloned INPUT / TEXTAREA / SELECT — it does NOT touch rendered text,
 * which the capture reproduces verbatim. This panel holds the first plaintext
 * credential this app has ever put in the DOM, so it has to be in the form the
 * capture already blanks.
 *
 * ⚠️ CORRECTED 2026-08-14 (T1076, board 900). This used to say the JPEG "is
 * stored in `feedback_submissions`". IT IS NOT. The kiosk feedback client has
 * ZERO sendBeacon and no upload path; its sink is a LOCAL DOWNLOAD
 * (feedback/widget.ts:759-786, createObjectURL -> link.download). The sole
 * writer of `feedback_submissions` is core/data/feedback-submissions-repo.ts:58
 * via server/feedback-capture.ts:360, which serves the SITE customer path, not
 * the kiosk. THE LEAK FIX ABOVE STANDS UNCHANGED — a credential rendered as
 * text is still captured verbatim into a file a person can carry away; only the
 * destination was wrong, never the exposure.
 *   ⚠️ `data-fb-opaque` is NOT a defence and must not be reached for here: it is
 *      read only by the click-trail recorder (feedback/clicks.ts:137), never by
 *      the screenshot filter.
 *   ⚠️ Neither is the screenshot filter's own marker — `WIDGET_MARKER_ATTR`,
 *      declared in feedback/clicks.ts and honoured at widget.ts:536. It is the
 *      natural answer and it is BARRED: scripts/check-killswitch.mjs greps the
 *      built client for that attribute's literal value and fails when the widget
 *      is switched OFF but the string is present. Putting it on a normal screen
 *      does exactly that — and so does merely NAMING it here, because the served
 *      sourcemap embeds this file's comments (measured: the OFF build is 0/5
 *      markers, and spelling the literal in this very comment turned
 *      client.js.map into 1/5 and failed the gate). Marking the panel would
 *      trade a picture leak for a broken killswitch.
 * The readonly input is also the better affordance for the job — Helen taps it
 * and the whole value selects, instead of touch-dragging up to a full stop.
 *
 * Smallest thing that works, not a user-management product (PRD R7): a list, an
 * add, a remove/restore and a reset. No roles, no invitations, no email.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  addOperatorAccount,
  listOperatorAccounts,
  resetOperatorAccountPassword,
  setOperatorAccountActive,
  type AdminOperator,
  type AdminOperatorEvent,
  type AdminOperators,
  type NewCredential,
} from '../../lib/actions.js';
import {
  fetchPushServerStatus,
  pushCoverageWarning,
  pushFailureCopy,
  type PushServerStatus,
} from '../../lib/actions.js';
import {
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  readDeviceState,
  syncOnScreenOpen,
  type PushDeviceState,
} from '../../lib/push-register.js';
import { shortDateTime } from '../../lib/format.js';
import { Banner } from '../../components/Banner.js';

export type OperatorsProps = {
  /** The signed-in operator's DISPLAY NAME, as `GET /api/me` returned it and the
   *  rail renders it (store `state.operator`). Threaded down rather than fetched
   *  again, and deliberately not taken from anything the client typed: the whole
   *  point of M6 is that the identity on screen is the server's. `null` only
   *  while the store has no session, which this screen is never reached without.
   *  Display names are UNIQUE by migration v23's `lower(display_name)` index and
   *  are never re-issued, so this one value identifies exactly one row. */
  currentOperator: string | null;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: AdminOperators };

/** What each recorded act reads as, in plain words. An unknown kind falls back
 *  to the raw verb rather than being hidden: a row this build does not
 *  understand still has to be visible, because the point of the list is that
 *  nothing done here is invisible. */
function eventPhrase(kind: string): string {
  switch (kind) {
    case 'created':
      return 'added';
    case 'disabled':
      return 'removed access for';
    case 'enabled':
      return 'restored access for';
    case 'password-reset':
      return 'reset the password for';
    default:
      return kind;
  }
}

/** The two UNIQUE keys, folded the way the database folds them, so the local
 *  duplicate check below cannot disagree with the server about what "already
 *  used" means. Mirrors core/data/operators-repo.ts's normaliseUsername and
 *  normaliseDisplayName + migration v23's `lower(display_name)` index; it is NOT
 *  imported from there because that module pulls in `pg` and would drag a
 *  node-only dependency into the browser bundle. Drift can only ever cost the
 *  operator the server's older combined sentence — the 409 stays authoritative,
 *  so a fold that disagrees can never turn a refusal into an accept. */
const foldLogin = (raw: string): string => raw.normalize('NFKC').trim().toLowerCase();
const foldDisplayName = (raw: string): string =>
  raw.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * THE MOST RECENT MOMENT A PUSH SERVICE ACCEPTED AN ALERT for ANY of this
 * operator's devices, in Helen's words — or the literal `never` (T2016 G2).
 *
 * ⛔ "ACCEPTED", NEVER "REACHED"/"SHOWN"/"DELIVERED". A 2xx from Apple means the
 * push service took custody of the alert. It is not a promise that the iPad woke,
 * that the banner drew, or that anyone saw it, and the panel must not claim one.
 *
 * ⛔ CALLERS MUST NOT PASS A `null` STATUS THROUGH HERE. "the server answered and
 * no device has ever succeeded" and "we could not ask the server" are different
 * facts; see the render site.
 *
 * ⚠️ AN UNPARSEABLE STAMP CAN NEVER WIN, and that is also the malformed guard:
 * `NaN > x` and `NaN <= x` are BOTH false, so junk is skipped, and if every stamp
 * is junk this returns `never` rather than the dangling label `shortDateTime`'s
 * `''` (format.ts) would leave behind.
 *
 * simplified: no per-device identity, upgrade when a device join key exists.
 * `GET /api/push/status` returns no endpoint by design (it is a bearer
 * capability), so this browser cannot tell which row is the iPad it is running
 * on. The render site therefore counts devices instead of naming one.
 *
 * ⛔⛔ `stamped` IS THE COUNT THE COPY MAY CLAIM, AND IT IS NOT `devices.length`.
 * The rule: NEVER claim more devices than actually carry a timestamp. A pair
 * `[null, <stamp>]` is two registered devices and ONE acceptance, so "across
 * your 2 devices" would assert an acceptance for a device that has never had
 * one — the same overstatement class as the arm this board retired. Counted
 * here rather than at the render site so the number and the timestamp can never
 * be derived from two different rules.
 */
type LastAccepted = {
  /** The most recent parseable stamp across the operator's devices, or null. */
  newest: string | null;
  /** How many devices carry a PARSEABLE stamp — never `devices.length`. */
  stamped: number;
  /** How many devices are registered, stamped or not. */
  total: number;
};

function lastAccepted(devices: PushServerStatus['devices']): LastAccepted {
  let newestMs = Number.NEGATIVE_INFINITY;
  let newest: string | null = null;
  let stamped = 0;
  for (const d of devices) {
    if (d.lastSuccessAt === null) continue;
    const ms = new Date(d.lastSuccessAt).getTime();
    // ⚠️ Junk is not a stamp, for the COUNT as well as for the winner. Testing
    // NaN explicitly rather than leaning on `!(ms > newestMs)` being false:
    // that guard keeps junk from WINNING, but it would still have counted.
    if (Number.isNaN(ms)) continue;
    stamped += 1;
    if (!(ms > newestMs)) continue;
    newestMs = ms;
    newest = d.lastSuccessAt;
  }
  return { newest, stamped, total: devices.length };
}

/**
 * The label, in Helen's words, claiming ONLY what `stamped` supports.
 *
 * ⛔ "ACCEPTED", NEVER "REACHED"/"SHOWN"/"DELIVERED" — see `lastAccepted`.
 * With nothing stamped the label names no device at all: "never" is a fact about
 * the account, and pinning it to "this iPad" would claim knowledge of which row
 * this browser is, which the status read deliberately withholds.
 */
function lastAcceptedLabel({ newest, stamped, total }: LastAccepted): string {
  // One device (or none): "this iPad" claims nothing the count cannot support, and
  // `never` here is a true statement about the only row there is.
  if (total <= 1) return 'Last alert Apple accepted for this iPad: ';
  // ⛔ THE THREE MULTI-DEVICE ARMS, AND THE MIDDLE ONE IS THE FIX. "across your N
  // devices" may be said ONLY when all N carry a stamp; a pair [null, <stamp>] is
  // two devices and ONE acceptance, and claiming both would assert an acceptance
  // for a device that has never had one.
  if (newest === null) return `Last alert Apple accepted on any of your ${String(total)} devices: `;
  if (stamped === total) return `Last alert Apple accepted across your ${String(total)} devices: `;
  return `Last alert Apple accepted on ${String(stamped)} of your ${String(total)} devices: `;
}

/** The whole line. ONE traversal, so the count and the timestamp cannot disagree. */
function lastAcceptedLine(devices: PushServerStatus['devices']): string {
  const accepted = lastAccepted(devices);
  return (
    lastAcceptedLabel(accepted) +
    (accepted.newest === null ? 'never' : shortDateTime(accepted.newest))
  );
}

/**
 * NOTIFICATIONS ON THIS iPAD (T1141 M5).
 *
 * ⛔ IT LIVES ON THIS SCREEN AND NOT ON A RAIL DESTINATION OF ITS OWN. The rail
 * is at 8 items against the ~700px plane (R11), clearing it by single-digit
 * pixels in the worst badge state, and a ninth row does not fit — so a new rail
 * item would push its own bottom anchor below the fold on the very tablet this
 * feature is for. ⚠️ NO FIGURE IS QUOTED HERE, and that is the second sweep of
 * this sentence: it carried `699px` and `a ninth row takes it to 755px
 * (Rail.tsx)`, both measured on the PRE-R6 rail, and `Rail.tsx` no longer states
 * either. DESIGN.md section 4.3 holds the live per-row heights, the badge state
 * they were taken in and the command that re-derives them, in one place.
 * And a screen with no rail
 * entry is "a blank pane waiting to be reached by a future typo"
 * (`store/state.ts`). "Who can sign in" is the nearest true home: both answer
 * "what can this person, on this device, do".
 *
 * ⛔ NOTHING SECRET IS RENDERED AS TEXT, and on this screen that is a rule with a
 * history (see the file header): the feedback widget photographs the live DOM
 * and blanks INPUT VALUES ONLY, never rendered text. A subscription's
 * `endpoint`/`p256dh`/`auth` are a BEARER CAPABILITY — anyone holding them can
 * put arbitrary text on a locked operator iPad — so none of the three is fetched
 * by this component, and `GET /api/push/status` does not return them in the
 * first place. What renders here is a state word and a count-free sentence.
 *
 * ⛔ NO `style={}` (banned by `check:copy`); every class is declared in app.css.
 */
function NotificationsOnThisDevice() {
  const [device, setDevice] = useState<PushDeviceState | 'checking'>('checking');
  /**
   * What the SERVER says, or `null` when the status read failed.
   *
   * ⛔ `null` IS NOT `push_enabled: false` AND IS NOT "no devices". A failed read
   * must render as neither: the first reads as "this server holds no VAPID
   * keypair" and the second as "your iPad is not registered", and acting on
   * either when we simply could not ask is how an operator re-runs a ceremony on
   * a device that was working.
   *
   * ⚠️ IT USED TO BE JUST `pushEnabled` (T1141 review, F1). The server has sent
   * `subscription_count`, `stale_after_ms` and a per-row `last_success_stale`
   * since M6, and this screen dropped all three - so `readDeviceState()`, which
   * asks the BROWSER only, was the whole truth the chip rendered.
   */
  const [server, setServer] = useState<PushServerStatus | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // Same synchronous double-tap latch as the roster actions above, for the same
  // measured reason: `busy` and `disabled` are both fixed at render time.
  const inFlight = useRef(false);
  const pushNoticeRef = useRef<HTMLDivElement>(null);

  const refresh = () => {
    void readDeviceState().then(
      (s) => setDevice(s),
      () => setDevice('unsupported'),
    );
    void fetchPushServerStatus().then(
      (s) => setServer(s),
      () => setServer(null),
    );
  };

  useEffect(() => {
    // ⚠️ THE REPAIR FOR A LAUNCH-TIME FAILURE, not a duplicate of it. The launch
    // sync in index.tsx runs before anyone has signed in on a cold start, so its
    // POST answers 401 and is swallowed. This screen can only be reached WITH a
    // session, so running the same sync here is the point at which a device that
    // silently failed at launch becomes registered again.
    void syncOnScreenOpen().then(refresh, refresh);
  }, []);

  const run = async (action: () => Promise<{ ok: true } | { ok: false; message: string }>, okText: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      setNotice(result.ok ? { kind: 'ok', text: okText } : { kind: 'error', text: result.message });
    } catch (e: unknown) {
      setNotice({ kind: 'error', text: pushFailureCopy(e, 'change notifications on this iPad') });
    } finally {
      inFlight.current = false;
      setBusy(false);
      refresh();
    }
  };

  // ⚠️ Where the caret goes after an action, exactly as the roster half does it:
  // the control that was tapped is `disabled` in flight, and the browser answers
  // that by blurring it down to <body>.
  useEffect(() => {
    if (notice === null) return;
    pushNoticeRef.current?.focus();
  }, [notice]);

  /**
   * ⛔⛔ THE SERVER CAN CONTRADICT THE BROWSER, AND WHEN IT DOES THE SCREEN MUST
   * NOT READ AS HEALTHY (T1141 review, F1). `readDeviceState()` asks this device
   * only: a granted permission plus a live `PushSubscription`. Both can be
   * perfectly true while the server holds ZERO rows for this operator - the
   * launch-time re-registration swallows its failures by design, so a 409 on a
   * colleague's iPad or a 429 on a heavy day leaves the browser looking subscribed
   * and the server holding nothing. A green chip in that state is a false
   * statement about whether alerts will arrive, which is worse than no chip.
   */
  const serverCannotSend = server !== null && !server.pushEnabled;
  const coverage = device === 'on' && !serverCannotSend ? pushCoverageWarning(server) : null;
  /** The ONE condition under which this screen may say "on" in green. */
  const covered = device === 'on' && !serverCannotSend && coverage === null;
  /** ⚠️ The server's own staleness rule, rendered rather than restated, so the
   *  sentence cannot drift from the period the server actually measures. */
  const staleDays = Math.round((server?.staleAfterMs ?? 0) / 86_400_000);

  const statusWord =
    device === 'checking'
      ? 'Checking'
      : device === 'on'
        ? covered
          ? 'Notifications on'
          : 'Check notifications'
        : device === 'blocked'
          ? 'Not allowed on this iPad'
          : device === 'unsupported'
            ? 'Not available here'
            : 'Notifications off';

  return (
    <div class="op-push">
      <h3 class="screen-sub">Notifications on this iPad</h3>
      {/* ⛔ THE FIRST SENTENCE WENT; THE SECOND EARNED ITS PLACE (T1749 M10 rework 1,
          Captain's live pass item 6). It read "When this is on, this iPad shows an
          alert the moment a new enquiry comes in, even when the app is not open. It
          applies to this iPad only, not to anyone else." The first sentence explains
          what a notification IS, which the heading directly above already says
          ("Notifications on this iPad") — that is the explanatory paragraph the rule
          rejects. The second is the opposite: the DEVICE SCOPE is invisible from the
          control and carries a real consequence, because Helen turning this on could
          reasonably believe she had turned it on for Owen too, and would then trust an
          alert that never reaches him. A hint that prevents a wrong belief about who
          is covered is exactly what Q139 means by earning its place. */}
      <p class="helper-note">This applies to this iPad only, not to anyone else.</p>
      <p class="op-push-status">
        <span class={`chip ${covered ? 'chip--ok' : 'chip--quiet'}`}>{statusWord}</span>
      </p>

      {/* ⛔⛔ THREE RENDER STATES, AND `server === null` IS THE ONE THAT MUST STAY
          SILENT (T2016 G2). `server` is null on a FAILED STATUS READ *and* on
          EVERY FIRST PAINT (the useState initial value above). Painting `never`
          there tells Helen "your iPad has never been reached" when the truth is
          "we could not ask" — the exact conflation this component's own docblock
          forbids, and the doctrine `pushCoverageWarning` holds when it returns
          null rather than a warning for a null status.
          The other two: a timestamp exists → render it; the server answered and
          nothing has ever succeeded → the literal `never`. */}
      {server !== null && (
        <p class="helper-note">{lastAcceptedLine(server.devices)}</p>
      )}

      {notice && (
        <Banner kind={notice.kind} elementRef={pushNoticeRef} tabIndex={-1}>
          {notice.text}
        </Banner>
      )}

      {device === 'blocked' && (
        <Banner kind="warn">
          This iPad has been told not to show notifications for this app. That can only be changed in
          the iPad Settings app, under Notifications. Nothing on this screen can turn it back on.
        </Banner>
      )}

      {/* ⛔ THIS COPY MUST NAME THE INSTALL STEP, NOT JUST THE DEVICE, AND THE
          REASON IS AN iOS RULE WE CANNOT DETECT AROUND. On iOS `PushManager` is
          only defined when the app is running from the HOME SCREEN ICON, never
          in a Safari tab (Apple docs, MDN BCD, caniuse note 7, WebKit 18.4 --
          four independent sources, see docs/research). So the ordinary first
          step on ANY new iPad, including the right one, lands here.
          ⚠️ The earlier wording was "open the app on the office iPad", which is
          circular in exactly that case: there is no icon to open yet. It only
          parsed as "you are on the wrong device", which is the LESS likely
          reading during setup of a second device, and this feature explicitly
          supports more than one.
          ⇒ `isPushSupported()` genuinely cannot tell "wrong device" from "not
          installed yet", so the copy stays broad about the CAUSE and precise
          about the REMEDY. Do not narrow it back to a device instruction
          without first adding a `display-mode: standalone` probe to tell the
          two apart. */}
      {device === 'unsupported' && (
        <Banner kind="info">
          Notifications can only be switched on from the installed app icon, not from a Safari tab.
          On this iPad: tap the Share button, choose Add to Home Screen, then open the app from that
          icon and try again.
        </Banner>
      )}

      {/* ⛔ "REGISTERED" AND "THE SERVER CAN SEND" ARE DIFFERENT FACTS, and this
          banner exists because a green control on its own is not evidence. A
          device can register perfectly against a server with no VAPID
          configuration, which is the DEFAULT state of this repo, and then never
          receive anything with nothing anywhere saying so. */}
      {device === 'on' && serverCannotSend && (
        <Banner kind="warn">
          This iPad is signed up, but notifications are not switched on for the office yet, so
          nothing will arrive until Alex finishes setting them up.
        </Banner>
      )}

      {/* ⛔ THE OTHER HALF OF THE SAME PROPERTY (T1141 review, F1): the office CAN
          send, this browser holds a live subscription, and the server still
          disagrees about whether this operator is covered. Two disagreements, two
          remedies, so two sentences - and neither blames the operator, because
          neither cause is anything she did.
          ⚠️ The stale arm deliberately says "one of the iPads registered to you"
          rather than "this iPad": the quiet row may be another device on the same
          account, and this screen cannot tell which. `GET /api/push/status`
          returns no endpoint (endpoint + keys IS the capability), so there is no
          mapping from a row to the device in her hand, and inventing a confident
          "this one" would send her to the wrong iPad. */}
      {coverage === 'server-has-no-record' && (
        <Banner kind="warn">
          This iPad looks switched on, but the office system has no record of it, so alerts will not
          arrive here. Nothing you did caused this. Turn notifications off for this iPad and then on
          again; if this message stays, tell Alex.
        </Banner>
      )}
      {coverage === 'device-gone-quiet' && (
        <Banner kind="warn">
          One of the iPads registered to you has had no alert for more than {staleDays} days, so it
          may have stopped working. On that iPad, turn notifications off and then on again. If you
          are not sure which one it is, tell Alex.
        </Banner>
      )}

      {device !== 'unsupported' && device !== 'blocked' && (
        <div class="op-push-actions">
          <button
            type="button"
            class="btn btn--primary"
            disabled={busy || device === 'on' || device === 'checking'}
            onClick={() =>
              void run(enablePushOnThisDevice, 'Notifications are on for this iPad.')
            }
          >
            Turn notifications on for this iPad
          </button>
          <button
            type="button"
            class="btn btn--quiet"
            disabled={busy || device !== 'on'}
            onClick={() =>
              void run(disablePushOnThisDevice, 'Notifications are off for this iPad.')
            }
          >
            Turn notifications off for this iPad
          </button>
        </div>
      )}
    </div>
  );
}

export function Operators({ currentOperator }: OperatorsProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  // The outcome of the last Copy, TAGGED WITH THE PASSWORD IT WAS ABOUT rather
  // than reset by an effect. A `copied` boolean plus
  // `useEffect(() => setCopied(false), [credential])` looked equivalent and was
  // not: Preact defers effects, so the stale-clearing run landed AFTER the
  // operator had already pressed Copy and wiped the confirmation she had just
  // been given (measured). Deriving it from the value it describes removes the
  // ordering question altogether.
  const [copyResult, setCopyResult] = useState<{ password: string; text: string } | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const credentialRef = useRef<HTMLDivElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  // ⛔ THE DOUBLE-SUBMIT LATCH, AND IT CANNOT BE `busy` (T642 M6 round B, UX
  // finding 6). `if (busy) return` reads a value fixed at RENDER time, and
  // `setBusy(true)` does not re-render before the next click in the same task —
  // so two fast taps both saw `false` and both issued the request (`disabled` is
  // equally late for the same reason). A ref is written and read synchronously,
  // which is the only thing that closes the window. `busy` stays: it is what
  // greys the controls out, which is a different job.
  const inFlight = useRef(false);
  // The generated password, held in component state ONLY: check-copy.sh bans
  // localStorage / sessionStorage / document.cookie in client source, and this
  // is the one value in the app that most obviously must not be persisted
  // anywhere a later reader could find it. It disappears on navigation, which is
  // why the panel says so out loud.
  const [credential, setCredential] = useState<NewCredential | null>(null);

  /**
   * ⭐ T939 M5 — THE SECOND READ IS GONE, AND SO IS THE NURSE SECTION IT FED.
   *
   * This screen carried a `Promise.all` of the operator roster and the portal's
   * nurse access review, because "who can get in" was one question Helen asked
   * once. Alex's control-centre ruling moves nurse management to its own rail
   * destination so it appears in exactly ONE place in the product (P0-2), and
   * with it went `decisions`, `toggleDecision`, `buildCsv`, `onCopyCsv`,
   * `downloadCsv` and `csvCopied`.
   *
   * ⛔ THE POSITIONAL-BIND PIN DID NOT DIE WITH THE ARRAY — IT MOVED, and this
   * note is what stops it evaporating. `Admin.tsx:183-190` destructures SEVEN
   * look-alike `{id,name,active}` reads by position, and its own comment warns
   * that appending a fetch in one milestone and another in a second silently
   * binds the WRONG name and still compiles. That array is still unguarded, and
   * the guarded stand-in is now the FOUR-read `Promise.all` in
   * screens/admin/NursingHomes.tsx, whose spec swaps positions as a mutation.
   * A single read cannot carry that pin, so collapsing this one without
   * re-siting it would have retired the guard with nothing behind it.
   */
  const load = () => {
    void listOperatorAccounts().then(
      (data) => setState({ kind: 'ready', data }),
      () => setState({ kind: 'error' }),
    );
  };
  useEffect(load, []);

  /**
   * WHERE THE CARET GOES AFTER AN ACTION (T642 M6 round B, UX finding 4).
   *
   * MEASURED before this existed: after add, disable, enable, reset AND both
   * server refusals, `document.activeElement` was `<body>` — because the control
   * that was clicked is `disabled` while the request is in flight and the browser
   * blurs a disabled element. On a screen whose whole shape is "one action per
   * row, many rows", that means a keyboard-only operator re-Tabs from the top of
   * the page after every single action. `role="status"` / `role="alert"` already
   * carry the words to a screen reader; they do nothing for the caret.
   *
   * Keyed on the notice OBJECT (a fresh one per action), so it fires for every
   * outcome including the two local refusals, and not on mount — `notice` starts
   * null, and `run()` clears it to null before awaiting. The credential panel
   * wins when one appeared, because a generated password is the thing the
   * operator came for and it is the part that cannot be shown again.
   */
  useEffect(() => {
    if (notice === null) return;
    (credentialRef.current ?? noticeRef.current)?.focus();
  }, [notice]);

  const run = async (
    action: () => Promise<{ ok: true; value: NewCredential | null } | { ok: false; message: string }>,
    okText: string,
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        return;
      }
      if (result.value !== null) setCredential(result.value);
      setNotice({ kind: 'ok', text: okText });
      load();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const onAdd = () => {
    const login = username.trim();
    const shown = displayName.trim();
    if (login === '' || shown === '') {
      setNotice({ kind: 'error', text: 'Both a login name and a display name are needed.' });
      return;
    }
    // WHICH NAME COLLIDED, answered before the round trip (T642 M6 round B, UX
    // finding 7). The server refuses both keys with ONE sentence naming both, so
    // the operator had to guess which of the two fields to change — and the
    // display-name rule is the non-obvious one worth spelling out: a display name
    // is reserved FOREVER, including a leaver whose access was removed, because
    // every quote they priced still carries it as the actor. The roster this
    // screen already loaded holds disabled accounts too, which is exactly what
    // makes that answerable here. The 409 below stays as the authority for the
    // race (two adds of one name in the same second).
    const roster = state.kind === 'ready' ? state.data.operators : [];
    if (roster.some((o) => foldDisplayName(o.displayName) === foldDisplayName(shown))) {
      setNotice({
        kind: 'error',
        text: 'That display name is already used. Names are never reused, even after someone loses access, so the quotes they priced still say who priced them. Pick another.',
      });
      return;
    }
    if (roster.some((o) => foldLogin(o.username) === foldLogin(login))) {
      setNotice({ kind: 'error', text: 'That login name is already used. Pick another.' });
      return;
    }
    void run(async () => {
      const r = await addOperatorAccount(login, shown);
      if (r.ok) {
        setUsername('');
        setDisplayName('');
      }
      return r;
    }, `${shown} can sign in now.`);
  };

  /**
   * Copy the one-time password to the clipboard.
   *
   * The alternative was a precise touch-drag that has to stop short of a full
   * stop, on a tablet, for a value that cannot be shown again. Where the
   * Clipboard API is not available (an insecure origin, or an older tablet
   * browser) this does NOT fail silently: it selects the field so the operator's
   * own copy gesture has something to work with, and says so. Nothing is written
   * to any store — check-copy.sh bans the persistent sinks in client source, and
   * this is the one value in the app that most obviously must not be persisted.
   */
  const onCopyPassword = () => {
    const value = credential?.password ?? '';
    // Typed OPTIONAL against lib.dom's non-optional declaration on purpose: it
    // is genuinely absent on an insecure origin and in older tablet browsers,
    // and the fallback below is the whole reason this reads it defensively.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    // Reported INSIDE the panel, not through `notice`: a notice moves focus to
    // itself (see the effect above), which would pull the caret straight back
    // off the field this branch just selected for her.
    const unavailable = () => {
      passwordRef.current?.focus();
      passwordRef.current?.select();
      setCopyResult({
        password: value,
        text: 'Copying is not available on this browser. The password is selected, so copy it yourself.',
      });
    };
    if (!clipboard) {
      unavailable();
      return;
    }
    clipboard.writeText(value).then(() => setCopyResult({ password: value, text: 'Copied.' }), unavailable);
  };

  const operators: AdminOperator[] = state.kind === 'ready' ? state.data.operators : [];
  const events: AdminOperatorEvent[] = state.kind === 'ready' ? state.data.events : [];

  return (
    // T933 F1 — the pane wrapper this screen LOST when T928 promoted it out of
    // <Admin>. <Admin> renders `screen-pane screen-pane--wide`; a promoted arm
    // renders straight into `.content`, which declares no padding, so the <h1>
    // sat flush against the navy rail at x=208 while the shell's own Back
    // control sat at x=240. Measured at every width 760-1440.
    <div class="screen-pane screen-pane--wide">
      {/* T928 slice 1: an <h1>, and no in-screen Back. This was a SUB-VIEW of
          the price-book screen and carried both; it is now a rail destination
          of its own, so its title is the page's top heading, and App.tsx
          already renders the app's single Back control for every screen except
          home (`canGoBack`). A second control here would also put two buttons
          named exactly "Back" on one screen, which an existing e2e query
          resolves with `exact: true`. */}
      <div class="book-list-head">
        <h1 class="screen-title" tabIndex={-1}>Who can sign in</h1>
      </div>

      {/* ⭐⭐ T1749 M7 (C6) — THE ATTRIBUTION NOTE, MOVED HERE FROM THE NURSING
          REQUESTS TAB, WHERE IT WAS A STANDING AMBER PARAGRAPH OVER EVERY ROW.
          Alex, 2026-09-11: "we need to get rid of this text, it gives this screen
          a cluttered view". The fact is kept and re-homed rather than dropped,
          because it is a fact about WHO CAN SIGN IN, which is this screen, and not
          about any nursing-home request.

          ⛔ THE FAILURE MODE IT STILL GUARDS is the kiosk's named-operator chrome
          implying an attribution the portal side does not have: everywhere else
          Helen is signed in as herself, so it is reasonable to assume her portal
          actions are recorded against her. They are not, because the portal keeps
          no operator identity at all. It states the TRADE as well as the gap,
          because the trade is what makes it defensible rather than merely
          disclosed: the shared passcode this replaced meant nobody could tell who
          acted at all.
          ⚠️ ABOVE THE PUSH PANEL, NOT INSIDE IT. Board 2016's "Last alert Apple
          accepted" line and its THREE render states (a stamp, the literal `never`,
          and silence on a null status read) are untouched: a sentence added into
          that panel would sit between its status chip and the line, and the panel
          is graded on exactly what it renders.
          ⚠️ `.helper-note` IS THIS SCREEN'S QUIET SENTENCE (the push panel uses it
          for its own two lines), not `.field-hint`: this is not a hint under a
          field, and Q139's copy rule is explicit that a hint says what shape an
          ANSWER takes. */}
      <p class="helper-note">
        Anyone who can sign in here can also price, decline or cancel nursing home
        requests, and the portal records those as coming from the business rather
        than from the person by name. That is still a step up from the shared
        passcode it replaces, where nobody could tell who acted at all.
      </p>

      {notice && (
        <Banner kind={notice.kind} elementRef={noticeRef} tabIndex={-1}>
          {notice.text}
        </Banner>
      )}

      {credential && (
        /* ⭐ T1749 M10 (Step 2d) — THE LIBRARY'S NOTICE, NOT A HAND-WRITTEN
            BANNER. This was `<div class="banner banner--ok op-cred" role="status"
            tabIndex={-1} ref={credentialRef}>`: the last app site in either tree
            that hand-wrote `banner--<kind>`, which `Notice.tsx:79` composes. Both
            props it needs already existed (`NOTICE_PROP_KEYS`), and `kind="ok"`
            derives the `role="status"` the element carried — the component owns
            that mapping, which is why there is no `role` prop to pass.
            ⚠️ NOT ZERO-PIXEL, AND THE DELTA IS THE RECORD RATHER THAN A CLAIM
            THAT IT IS SMALL. `.op-cred` (`padding: var(--space-4)`, no descendant
            selector) is DELETED from `app.css` in this commit, so the box goes
            16px all round -> `card--row`'s 12px/16px and gains `.card`'s 12px
            corner; the 2px `--ok-600` edge, the white fill, the 640px measure and
            the 12px/0 margin are untouched. One panel, on one kiosk admin screen,
            behind a password reset. `.op-cred-lead` / `-value` / `-note` /
            `-actions` / `-copied` are SEPARATE names (three other screens wear
            some of them) and none is a descendant of `.op-cred`, so they stay. */
        <Banner kind="ok" elementRef={credentialRef} tabIndex={-1}>
          <p class="op-cred-lead">New password for {credential.username}</p>
          {/* ⛔ AN INPUT, NOT TEXT — see the file header. A rendered password is
              photographed verbatim by the feedback widget's DOM capture; an input
              VALUE is blanked by the redaction that capture already runs. The
              type MUST stay one the redaction covers: widget.ts's
              LABEL_INPUT_TYPES ('button','submit','reset','image') are the four
              it deliberately leaves alone. */}
          <input
            ref={passwordRef}
            class="op-cred-value"
            type="text"
            readOnly
            value={credential.password}
            aria-label={`New password for ${credential.username}`}
            autocomplete="off"
            autocapitalize="none"
            spellcheck={false}
            onFocus={(e) => (e.currentTarget as HTMLInputElement).select()}
          />
          <p class="op-cred-note">Give it to them now. It cannot be shown again.</p>
          <div class="op-cred-actions">
            <button type="button" class="btn btn--quiet btn--inline" onClick={onCopyPassword}>
              Copy password
            </button>
            <button
              type="button"
              class="btn btn--quiet btn--inline"
              onClick={() => {
                setCredential(null);
                // The control being clicked is about to unmount, which would drop
                // the caret to <body> — the same defect finding 4 is about.
                noticeRef.current?.focus();
              }}
            >
              Hide password
            </button>
            <span class="op-cred-copied" role="status">
              {copyResult !== null && copyResult.password === credential.password
                ? copyResult.text
                : ''}
            </span>
          </div>
        </Banner>
      )}

      {/* T1141 M5 — rendered OUTSIDE the roster's load states on purpose: a
          failed `GET /api/operators` must not take the notification control away
          with it. The two reads are unrelated. */}
      <NotificationsOnThisDevice />

      {state.kind === 'loading' && <p class="helper-note">Getting the list of people…</p>}
      {state.kind === 'error' && (
        <Banner kind="error">
          Loading the list of people failed. Try again in a moment.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
        </Banner>
      )}

      {state.kind === 'ready' && (
        <>
          <ul class="op-list" role="list">
            {operators.map((o) => (
              <li key={o.username} class="op-row">
                {/* `title` keeps the WHOLE name reachable while the CSS clips it
                    to one line: a 64-character name used to wrap and take the
                    row from 55px to 115px, pushing its own chip and buttons
                    away from it (worse on a narrow tablet). The text node is
                    untouched, so it is still the full string to a screen reader
                    and to any spec matching on it. */}
                <span class="op-name op-name--person" title={o.displayName}>{o.displayName}</span>
                {/* Whose row is mine. `currentOperator` is the server's own
                    display_name, so this cannot drift from what the rail shows
                    or from what the audit trail records — and it makes the
                    "you cannot remove your own access" refusal read as expected
                    rather than as a bug. Its own element, never appended to the
                    name: the name has to stay an exact-match text node. */}
                {currentOperator !== null && o.displayName === currentOperator && (
                  <span class="op-you">(you)</span>
                )}
                <span class="op-login">{o.username}</span>
                <span class={`chip ${o.active ? 'chip--ok' : 'chip--quiet'}`}>
                  {o.active ? 'Can sign in' : 'No access'}
                </span>
                {/* ⛔ THE DESTRUCTIVE HALF LOOKS DESTRUCTIVE, and only that half.
                    Measured before this: "Remove access" and "Reset password" sat
                    side by side with identical computed colours, and "Restore
                    access" — positive and reversible — was styled exactly like
                    its opposite. Reusing the house .btn--destructive (Inbox's
                    dismiss confirm already wears it) rather than inventing a
                    vocabulary; the row scopes a matching hairline onto it so the
                    weight is not carried by colour alone. Reset stays quiet on
                    purpose: it revokes sessions, but it is the RECOVERY path. */}
                <button
                  type="button"
                  class={o.active ? 'btn btn--quiet btn--destructive' : 'btn btn--quiet'}
                  disabled={busy}
                  onClick={() =>
                    void run(
                      async () => {
                        const r = await setOperatorAccountActive(o.username, !o.active);
                        return r.ok ? { ok: true as const, value: null } : r;
                      },
                      o.active ? `${o.displayName} can no longer sign in.` : `${o.displayName} can sign in again.`,
                    )
                  }
                >
                  {o.active ? `Remove access for ${o.username}` : `Restore access for ${o.username}`}
                </button>
                <button
                  type="button"
                  class="btn btn--quiet"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => resetOperatorAccountPassword(o.username),
                      `New password made for ${o.displayName}.`,
                    )
                  }
                >
                  Reset password for {o.username}
                </button>
              </li>
            ))}
          </ul>

          <div class="op-add">
            <h3 class="screen-sub">Add someone</h3>
            <input
              class="login-input"
              type="text"
              placeholder="Login name"
              aria-label="Login name"
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              value={username}
              onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
            />
            <input
              class="login-input"
              type="text"
              placeholder="Display name"
              aria-label="Display name"
              autocomplete="off"
              value={displayName}
              onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)}
            />
            <p class="helper-note">
              The display name goes on every quote and price change they make. It cannot be changed
              afterwards, so type it the way it should read.
            </p>
            {/* ⭐ board 2812 M5 (fix round 1, P2): SECONDARY, not gold. The screen showed two
                golds and this one sat at 1121 at 1024x700, below the plane; the push
                panel's "Turn notifications on for this iPad" (540) stays the one gold. */}
            <button type="button" class="btn btn--secondary" disabled={busy} onClick={onAdd}>
              Add operator
            </button>
          </div>


          <div class="op-activity">
            <h3 class="screen-sub">Recent changes</h3>
            {events.length === 0 && <p class="helper-note">Nothing has been changed here yet.</p>}
            <ul class="op-list" role="list">
              {events.map((e, i) => (
                <li key={`${String(i)}-${e.at}-${e.subject}-${e.kind}`} class="op-row">
                  <span class="op-name">
                    {e.actor} {eventPhrase(e.kind)} {e.subject}
                  </span>
                  <span class="op-login">{shortDateTime(e.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
